import { zodResolver } from '@hookform/resolvers/zod';
import { FormLabel } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { PhoneNumberUtil } from 'google-libphonenumber';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import apiClient from '../../../api/client';
import PhoneVerificationModal from '../../../components/phone-verification-modal';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../../../components/ui/input-otp';

const formSchema = z.object({
  oldPhoneNumber: z.string().min(1, 'Phone number is required').max(15, 'Phone number must be at most 15 digits long'),
  newPhoneNumber: z.string().min(1, 'Phone number is required').max(15, 'Phone number must be at most 15 digits long'),
  pin: z.string().length(6, 'PIN must be exactly 6 digits long')
});

type FormSchema = z.infer<typeof formSchema>;

enum PhoneUpdateFlowStep {
  EnterCurrentPhone,
  FaceScan,
  EnterPin,
  EnterNewPhone,
  Done
}

type PhoneValidateData = {
  phoneNumber: string;
  countryCode: string;
  type?: 'existing' | 'new';
}

type PhoneVerifyData = {
  phoneNumber: string;
  countryCode: string;
  otp: string;
}

const UpdatePhoneNumber: React.FC = () => {
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      oldPhoneNumber: '60',
      newPhoneNumber: '60'
    }
  });
  const [searchParams, setSearchParams] = useSearchParams();

  const currentStep = Number(searchParams.get('step')) as PhoneUpdateFlowStep || PhoneUpdateFlowStep.EnterCurrentPhone;

  const { register, getValues, setValue, watch, setError, formState: { errors }, clearErrors } = form;

  const onSubmit = (data: FormSchema) => {
    console.log('Form Data:', data);
  }

  const [openDialog, setOpenDialog] = useState(false);

  const phoneValidate = useMutation({
    mutationFn: async (data: PhoneValidateData) => {
      // Simulate an API call to update the phone number
      await apiClient.post('/users/phone/validate', data);
      return data; // Return data so it's available in onSuccess
    },
    onSuccess: (data) => {
      console.log('OTP sent successfully');

      const phoneNumber = data.type === 'existing' ? getValues('oldPhoneNumber') : getValues('newPhoneNumber');
      const phoneUtil = PhoneNumberUtil.getInstance();
      const number = phoneUtil.parseAndKeepRawInput(phoneNumber, 'MY');
      if (number.getNationalNumber() && number.getCountryCode()) {
        setVerifyPhone(`${number.getCountryCode()}-${number.getNationalNumber()}`);
      }
    },
    onError: (error) => {
      console.error('Error updating phone number:', error);

      setError('oldPhoneNumber', {
        type: 'manual',
        message: (error as any)?.response?.data?.error?.message || 'Invalid phone number'
      });
    }
  })

  const handleContinue = (data: FormSchema) => {
    clearErrors('oldPhoneNumber');
    const phoneUtil = PhoneNumberUtil.getInstance();
    try {
      const number = phoneUtil.parseAndKeepRawInput(data.oldPhoneNumber, 'MY');

      if (!phoneUtil.isValidNumber(number)) {
        throw new Error('Invalid phone number');
      }

      if (!number.getNationalNumber() || !number.getCountryCode()) {
        throw new Error('Phone number is incomplete');
      }

      phoneValidate.mutate({
        phoneNumber: number.getNationalNumber()?.toString() || '',
        countryCode: number.getCountryCode()?.toString() || '',
        type: 'existing'
      })

    } catch (error) {
      console.error('Error parsing phone number:', error);
      setError('oldPhoneNumber', {
        type: 'manual',
        message: (error as any)?.message || 'Invalid phone number'
      });
    }
  }

  const verifyOtpForCurrentPhone = useMutation({
    mutationFn: async (data: {
      otp: string;
    }) => {
      await apiClient.post('/users/verify/otp', data);
      return data;
    },
    onSuccess: (data) => {
      console.log('Phone number validated successfully');
      const params = new URLSearchParams(window.location.search);
      params.set('step', PhoneUpdateFlowStep.EnterPin.toString());
      setSearchParams(params);
    },
    onError: (error) => {
      console.error('Error validating phone number:', error);
      setError('oldPhoneNumber', {
        type: 'manual',
        message: (error as any)?.response?.data?.error?.message || 'Invalid phone number'
      });
    }
  });

  const handleVerifyOtpForCurrentPhone = (otp: string) => {
    verifyOtpForCurrentPhone.mutate({
      otp
    });
  }

  const validatePin = useMutation({
    mutationFn: async (pin: string) => {
      // Simulate an API call to validate the PIN
      await apiClient.post('/users/validate-pin', { pin });
      return pin; 
    },
    onSuccess: () => {
      const params = new URLSearchParams(window.location.search);
      params.set('step', PhoneUpdateFlowStep.EnterNewPhone.toString());
      setSearchParams(params);
    },
    onError: (error) => {
      console.error('Error validating PIN:', error);
      setValue('pin', '');
      setError('pin', {
        type: 'manual',
        message: 'Invalid PIN. Please try again.'
      });
    }
  });

  const handleCheckPin = (pin: string) => {
    if (pin.length < 6) {
      return;
    }

    validatePin.mutate(pin);

    // const params = new URLSearchParams(window.location.search);
    // params.set('step', PhoneUpdateFlowStep.EnterNewPhone.toString());
    // setSearchParams(params);
  }

  const handleContinueNewPhone = (data: FormSchema) => {
    clearErrors('newPhoneNumber');
    const phoneUtil = PhoneNumberUtil.getInstance();
    try {
      const number = phoneUtil.parseAndKeepRawInput(data.newPhoneNumber, 'MY');

      if (!phoneUtil.isValidNumber(number)) {
        throw new Error('Invalid phone number');
      }

      phoneValidate.mutate({
        phoneNumber: number.getNationalNumber()?.toString() || '',
        countryCode: number.getCountryCode()?.toString() || '',
        type: 'new'
      })
    } catch (error) {
      setError('newPhoneNumber', {
        type: 'manual',
        message: (error as any)?.response?.data?.error?.message || 'Invalid phone number'
      });
      console.error('Error parsing phone number:', error);
    }
  }

  const confirmChangePhone = useMutation({
    mutationFn: async (data: PhoneVerifyData) => {
      // Simulate an API call to confirm the phone number change
      await apiClient.put('/me/phone', data);
      return data; // Return data so it's available in onSuccess
    },
    onSuccess: () => {
      console.log('Phone number updated successfully');
      const params = new URLSearchParams(window.location.search);
      params.set('step', PhoneUpdateFlowStep.Done.toString());
      setSearchParams(params);
    },
    onError: (error) => {
      console.error('Error confirming phone number:', error);
    }
  });

  const handleVerifyOtpForNewPhone = (otp: string) => {
    const phoneUtil = PhoneNumberUtil.getInstance();
    const number = phoneUtil.parseAndKeepRawInput(getValues('newPhoneNumber'), 'MY');

    confirmChangePhone.mutate({
      phoneNumber: number.getNationalNumber()?.toString() || '',
      countryCode: number.getCountryCode()?.toString() || '',
      otp
    });
  }

  const handleCloseDialog = () => {
    setOpenDialog(false);
    const params = new URLSearchParams(window.location.search);
    params.delete('step');
    setSearchParams(params);
    form.reset();
  }

  const [verifyPhone, setVerifyPhone] = useState('');

  const titleMapping = {
    [PhoneUpdateFlowStep.EnterCurrentPhone]: 'Enter Old Phone Number',
    [PhoneUpdateFlowStep.FaceScan]: 'Face Scan',
    [PhoneUpdateFlowStep.EnterPin]: 'Enter PIN To Continue',
    [PhoneUpdateFlowStep.EnterNewPhone]: 'Enter New Phone Number',
  };

  const contentMapping = {
    [PhoneUpdateFlowStep.EnterCurrentPhone]: (
      <>
        <FormLabel className='mb-2'>Phone Number</FormLabel>
        <Controller
          name="oldPhoneNumber"
          control={form.control}
          render={({ field }) => (
            <PhoneInput
              country={'my'}
              value={field.value}
              onChange={(value: string) => {
                field.onChange(value);
                setValue('oldPhoneNumber', value);
              }}
              inputStyle={{ width: '100%' }}
              enableSearch={false}
              disableCountryCode={true}
              countryCodeEditable={true} // Allow users to edit the country code
              placeholder="Enter your phone number"
              autoFormat={false}
            />
          )}
        />
        {errors.oldPhoneNumber && (
          <p className='text-red-500 text-sm mt-1'>{errors.oldPhoneNumber.message}</p>
        )}

        <Button
          className='bg-[#5CB891] hover:bg-[#5CB891] mt-10 rounded-full'
          onClick={() => handleContinue(watch())}
          type='button'
        >
          Continue
        </Button>
      </>
    ),
    [PhoneUpdateFlowStep.FaceScan]: (
      <div>
        <p>Face scan verification.</p>
        {/* Add face scan component here */}
      </div>
    ),
    [PhoneUpdateFlowStep.EnterPin]: (
      <>
        <div className='flex flex-col items-center'>
          <p className='text-center text-sm mt-2 mb-8'>Please enter your 6-digit PIN</p>
          <Controller
            name="pin"
            control={form.control}
            render={({ field }) => (
              <InputOTP
                {...field}
                maxLength={6}
                value={field.value}
                onChange={(e) => {
                  field.onChange(e);
                  handleCheckPin(e);
                }}
                    >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                </InputOTPGroup>
                <InputOTPGroup>
                  <InputOTPSlot index={1} />
                </InputOTPGroup>
                <InputOTPGroup>
                  <InputOTPSlot index={2} />
                </InputOTPGroup>
                <InputOTPGroup>
                  <InputOTPSlot index={3} />
                </InputOTPGroup>
                <InputOTPGroup>
                  <InputOTPSlot index={4} />
                </InputOTPGroup>
                <InputOTPGroup>
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            )}
          />
          {errors.pin && (
            <p className='text-red-500 text-sm mt-2'>{errors.pin.message}</p>
          )}
        </div>
        
        <div className='mx-auto'>
          <Button variant={'link'} className='text-[#5B5959] mt-4'>
            Forget PIN?
          </Button>
        </div>

      </>

    ),
    [PhoneUpdateFlowStep.EnterNewPhone]: (
      <>
        <FormLabel className='mb-2'>Phone Number</FormLabel>
        <Controller
          name="newPhoneNumber"
          control={form.control}
          render={({ field }) => (
            <PhoneInput
              country={'my'}
              value={field.value}
              onChange={(value: string) => {
                field.onChange(value);
                setValue('newPhoneNumber', value);
              }}
              inputStyle={{ width: '100%' }}
              enableSearch={false}
              disableCountryCode={true}
              countryCodeEditable={true} // Allow users to edit the country code
              placeholder="Enter your phone number"
              autoFormat={false}
            />
          )}
        />
        {errors.newPhoneNumber && (
          <p className='text-red-500 text-sm mt-1'>{errors.newPhoneNumber.message}</p>
        )}

        <Button
          className='bg-[#5CB891] hover:bg-[#5CB891] mt-10 rounded-full'
          onClick={() => handleContinueNewPhone(watch())}
          type='button'
        >
          Continue
        </Button>
      </>
    ),
    [PhoneUpdateFlowStep.Done]: (

      <>
        <img
          src="/assets/images/update-phone-success.png"
          alt="Phone Update Success"
          className='w-32 mx-auto my-4'
        />

        <h3 className='text-center text-md font-semibold'>Your Phone Number has been updated.</h3>
        
        <div className='flex justify-between mt-5'>
          <p className='text-sm text-[#5B5959]'>
            Date & Time
          </p>

          <p className='text-sm text-[#5B5959] font-bold'>
            November 28, 2024 at 05:09 PM
          </p>
        </div>

        <Button
          className='bg-[#5CB891] hover:bg-[#5CB891] mt-10 rounded-full'
          onClick={handleCloseDialog}
          type='button'
        >
          Done
        </Button>
      </>
    )
  }

  return (
    <>
      <div className="flex items-center justify-between gap-2 border-t-1 py-4">
        <div className="flex flex-col gap-1">
          <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
            Update Phone Number
          </h3>
          <p className="text-sm text-gray-500">
            Phone number changed? Update it now
          </p>
          <p className="text-sm italic text-gray-500">
            Phone number last updated: July 13, 2024
          </p>
        </div>
        <Button
          type="button"
          className="min-w-24 disabled:bg-gray-700"
          onClick={() => {
            setOpenDialog(true);
          }}
        >
          Update
        </Button>
      </div>

      <Dialog open={openDialog} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-[24rem] rounded-3xl">
          {currentStep !== PhoneUpdateFlowStep.Done && (
            <DialogHeader className='mt-10'>
              <DialogTitle>{titleMapping[currentStep]}</DialogTitle>
            </DialogHeader>
          )}

          <div className="">
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col">
              {contentMapping[currentStep]}
            </form>
          </div>
        </DialogContent>
      </Dialog>

      <PhoneVerificationModal
        phoneNumber={verifyPhone}
        onClose={() => {
          setVerifyPhone('');
        }}
        onVerify={otp => {
          if (currentStep === PhoneUpdateFlowStep.EnterNewPhone) {
            handleVerifyOtpForNewPhone(otp);
          } else {
            handleVerifyOtpForCurrentPhone(otp);
          }
          setVerifyPhone('');
        }}
        isOpen={verifyPhone !== ''}
      />
    </>
  )
}

export default UpdatePhoneNumber;